const ScheduleSection = () => {
  return (
    <section id="schedule" className="py-24 bg-gradient-to-br from-sand-100 to-sage-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-primary-600 text-sm uppercase tracking-wider font-semibold mb-4 block">
            Schedule & Dates
          </span>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
            Your 4-Day Journey
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Each day is thoughtfully designed to guide you deeper into balance and self-discovery
          </p>
        </div>

        {/* Daily Schedule */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          {/* Day 1 */}
          <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-primary-600 font-bold text-lg">1</span>
              </div>
              <div>
                <h3 className="text-2xl font-serif font-bold text-gray-900">Day 1: Arrival & Grounding</h3>
                <p className="text-sage-600">Settle in. Slow down. Arrive into yourself.</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">4:00 PM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Welcome Ceremony</p>
                  <p className="text-sm text-gray-600">Traditional greeting, herbal teas, introductions & orientation</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">5:00 PM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Ayurvedic Consultation</p>
                  <p className="text-sm text-gray-600">Personal dosha assessment with an Ayurvedic expert</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">7:00 PM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Welcome Dinner</p>
                  <p className="text-sm text-gray-600">Choice of Sattvic Ayurvedic Meal or Light Vegetarian Meal</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">8:30 PM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Evening Meditation</p>
                  <p className="text-sm text-gray-600">Gentle grounding to prepare for the journey</p>
                </div>
              </div>
            </div>
          </div>

          {/* Day 2 */}
          <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-sage-600 font-bold text-lg">2</span>
              </div>
              <div>
                <h3 className="text-2xl font-serif font-bold text-gray-900">Day 2: Purification</h3>
                <p className="text-sage-600">Cleanse the body. Clear the mind. Rebalance energy.</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">6:30 AM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Herbal Tea & Morning Wake-Up Ritual</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">7:00 AM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Yoga & Pranayama</p>
                  <p className="text-sm text-gray-600">Sunrise activation flow for clarity and energy</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">8:30 AM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Breakfast</p>
                  <p className="text-sm text-gray-600">Sattvic or vegetarian, tailored to your dosha</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">10:00 AM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Ayurvedic Treatment</p>
                  <p className="text-sm text-gray-600">Personalized therapy (Abhyanga / Shirodhara / Marma)</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">12:00 PM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Expert Lecture: Understanding Your Dosha</p>
                  <p className="text-sm text-gray-600">Deep dive into Vata–Pitta–Kapha + practical lifestyle balancing tools</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">1:00 PM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Lunch</p>
                  <p className="text-sm text-gray-600">Ayurvedic dosha-aligned meal</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">2:30 PM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Pilates Session</p>
                  <p className="text-sm text-gray-600">Core strengthening & postural alignment for mind-body connection</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">4:00 PM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Beach Meditation</p>
                  <p className="text-sm text-gray-600">Ocean-connected breathing & guided presence</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">6:30 PM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Anahata (Heart-Centered) Session</p>
                  <p className="text-sm text-gray-600">Emotional release & compassion-focused practice</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">7:30 PM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Dinner</p>
                  <p className="text-sm text-gray-600">Sattvic or vegetarian</p>
                </div>
              </div>
            </div>
          </div>

          {/* Day 3 */}
          <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gold-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-gold-600 font-bold text-lg">3</span>
              </div>
              <div>
                <h3 className="text-2xl font-serif font-bold text-gray-900">Day 3: Integration & Digital Detox</h3>
                <p className="text-sage-600">Deep reset for your habits, mind, and energy systems.</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">6:30 AM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Herbal Tea & Silence</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">7:00 AM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Advanced Yoga Flow</p>
                  <p className="text-sm text-gray-600">Strengthening practice for alignment & grounding</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">8:30 AM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Breakfast</p>
                  <p className="text-sm text-gray-600">Light, detox-supportive Ayurvedic meal</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">9:00 AM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Vipassana-Style Silent Meditation</p>
                  <p className="text-sm text-gray-600">One hour of noble silence and inner observation</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">10:30 AM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Water-Based Experience</p>
                  <p className="text-sm text-gray-600">Healing ocean floating, mindful swimming, or water breathwork (led by Spanish nature/water specialists)</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">1:00 PM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Detox Lunch</p>
                  <p className="text-sm text-gray-600">Light sattvic dishes to support purification</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">2:00 PM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Pilates Session</p>
                  <p className="text-sm text-gray-600">Breathwork-integrated pilates to deepen body awareness & release tension</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">3:00 PM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Digital Wellbeing & Habit Detox</p>
                  <p className="text-sm text-gray-600">Rewiring dopamine habits, ending digital overstimulation, conscious phone use, building long-term digital hygiene & rituals for balance</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">5:00 PM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Mindful Games & Activities</p>
                  <p className="text-sm text-gray-600">Play-based emotional release + connection-building</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">7:30 PM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Dinner</p>
                  <p className="text-sm text-gray-600">Restorative vegetarian/sattvic meal</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">8:30 PM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Sharing Circle</p>
                  <p className="text-sm text-gray-600">Community reflection, integration & heart opening</p>
                </div>
              </div>
            </div>
          </div>

          {/* Day 4 */}
          <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-primary-600 font-bold text-lg">4</span>
              </div>
              <div>
                <h3 className="text-2xl font-serif font-bold text-gray-900">Day 4: Transformation & Departure</h3>
                <p className="text-sage-600">Seal your healing. Step into a lighter you.</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">6:30 AM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Herbal Tea</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">7:00 AM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Sunrise Meditation</p>
                  <p className="text-sm text-gray-600">Gratitude, grounding, and future intention-setting</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">8:30 AM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Breakfast</p>
                  <p className="text-sm text-gray-600">Nourishing closing meal</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">9:00 AM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Personal Swasthya Roadmap Session</p>
                  <p className="text-sm text-gray-600">Your customised lifestyle plan: food, rituals, breathwork, routines</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">11:00 AM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Closing Ceremony</p>
                  <p className="text-sm text-gray-600">Blessings, energy sealing, and final grounding</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-sm text-gray-500 w-20 flex-shrink-0 mt-1">1:00 PM</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Departure</p>
                  <p className="text-sm text-gray-600">A renewed, balanced, clear version of you</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Available Dates */}
        <div className="text-center">
          <h3 className="text-3xl font-serif font-bold text-gray-900 mb-8">Available Retreat Dates</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {/* March */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-transparent hover:border-primary-200 transition-all duration-300">
              <div className="text-primary-600 text-lg font-bold mb-2">March 2025</div>
              <div className="text-2xl font-serif font-bold text-gray-900 mb-2">Mar 18-21</div>
              <div className="text-sm text-gray-600 mb-4">Spring Awakening</div>
              <div className="inline-block bg-sage-100 text-sage-700 px-3 py-1 rounded-full text-xs font-medium">
                8 spots available
              </div>
            </div>

            {/* April */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-primary-200 transform scale-105">
              <div className="text-primary-600 text-lg font-bold mb-2">April 2025</div>
              <div className="text-2xl font-serif font-bold text-gray-900 mb-2">Apr 2-5</div>
              <div className="text-sm text-gray-600 mb-4">Perfect Weather Retreat</div>
              <div className="inline-block bg-gold-100 text-gold-700 px-3 py-1 rounded-full text-xs font-medium">
                Most Popular
              </div>
            </div>

            {/* May */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-transparent hover:border-primary-200 transition-all duration-300">
              <div className="text-primary-600 text-lg font-bold mb-2">May 2025</div>
              <div className="text-2xl font-serif font-bold text-gray-900 mb-2">May 14-17</div>
              <div className="text-sm text-gray-600 mb-4">Late Spring Renewal</div>
              <div className="inline-block bg-sage-100 text-sage-700 px-3 py-1 rounded-full text-xs font-medium">
                6 spots available
              </div>
            </div>

            {/* June */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-transparent hover:border-primary-200 transition-all duration-300">
              <div className="text-primary-600 text-lg font-bold mb-2">June 2025</div>
              <div className="text-2xl font-serif font-bold text-gray-900 mb-2">Jun 11-14</div>
              <div className="text-sm text-gray-600 mb-4">Summer Solstice Special</div>
              <div className="inline-block bg-sage-100 text-sage-700 px-3 py-1 rounded-full text-xs font-medium">
                5 spots available
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-8 max-w-2xl mx-auto">
            Limited to 12 participants per retreat to ensure personalized attention and intimate group dynamics. 
            Each retreat includes airport transfers from Fuerteventura Airport.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ScheduleSection;